import React, { useEffect, useState, useRef } from 'react';

interface XPTextProps {
  xpValue: number;
  positionX: number;
  positionY: number;
  onAnimationComplete: () => void;
}

const XPText: React.FC<XPTextProps> = ({ xpValue, positionX, positionY, onAnimationComplete }) => {
  const [opacity, setOpacity] = useState(1);
  const [yOffset, setYOffset] = useState(0);
  const [scale, setScale] = useState(1);
  const animationRef = useRef<number>(0);
  const startTimeRef = useRef<number>(0);
  const animationDuration = 1500; // 1.5 seconds instead of 2 seconds
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    startTimeRef.current = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTimeRef.current;
      const progress = Math.min(elapsed / animationDuration, 1);
      
      // Animation phases:
      // 1. Quickly scale up (0-10%)
      // 2. Stay visible while moving up (10-80%)
      // 3. Fade out (80-100%)
      
      if (progress < 0.1) {
        // Phase 1: Scale up
        setScale(1 + progress * 5); // Scale from 1 to 1.5
        setOpacity(1);
      } else if (progress < 0.8) {
        // Phase 2: Move up steadily
        setScale(1.5); // Maintain max scale
        setOpacity(1);
      } else {
        // Phase 3: Fade out
        const fadeProgress = (progress - 0.8) / 0.2; // Normalize to 0-1 for final 20%
        setOpacity(1 - fadeProgress);
        setScale(1.5);
      }
      
      // Update Y position (move up by 100px)
      setYOffset(-100 * progress);
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        // Animation complete
        onAnimationComplete();
      }
    };
    
    animationRef.current = requestAnimationFrame(animate);
    
    // Set a backup timeout in case animation is interrupted or performance issues occur
    timeoutRef.current = setTimeout(() => {
      onAnimationComplete();
    }, animationDuration + 500); // Add 500ms buffer
    
    return () => {
      cancelAnimationFrame(animationRef.current);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [onAnimationComplete, animationDuration]);

  return (
    <div 
      className="absolute pointer-events-none z-10" 
      style={{
        left: positionX,
        top: positionY + yOffset,
        opacity,
        transform: `translate(-50%, -50%) scale(${scale})`,
        transition: 'opacity 0.05s linear',
        pointerEvents: 'none'
      }}
    >
      <div className="text-yellow-300 font-bold text-xl drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)] animate-pulse-subtle pointer-events-none">
        <span className="bg-gradient-to-br from-yellow-200 to-yellow-400 text-transparent bg-clip-text pointer-events-none">+{xpValue} XP</span>
      </div>
    </div>
  );
};

export default XPText; 